import { useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Message from './Message';

function Messages({ messages, chatId, onRetryUpload, onSetTab }) {
  const currentUserId = useSelector((s) => s.user.authUserId);
  const uploads = useSelector((s) => s.uploads.byId);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const canSmoothScrollRef = useRef(false);

  // Merge real messages with pending uploads from Redux
  const displayMessages = useMemo(() => {
    const chatUploads = Object.values(uploads).filter(
      upload => upload.chatId === chatId && upload.type === 'message'
    );

    // Filter out completed uploads that have matching real messages
    const filteredUploads = chatUploads.filter(upload => {
      if (upload.status === 'complete' && upload.realMessageId) {
        // Check if real message with this ID already exists
        return !messages.some(m => m.id === upload.realMessageId);
      }
      return true;
    });

    // Convert uploads to message-like objects
    const uploadMessages = filteredUploads.map(upload => ({
      id: upload.id,
      chat_id: upload.chatId,
      sender_id: upload.senderId,
      body: upload.data.body,
      created_at: upload.createdAt,
      read_by: {},
      sending: upload.status === 'uploading',
      error: upload.status === 'error' ? upload.error : null,
      isUpload: true,
      attachments: upload.attachments || [],
    }));

    // Merge and sort by created_at
    const allMessages = [...messages, ...uploadMessages];
    return allMessages.sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );
  }, [messages, uploads, chatId]);

  // Enable smooth scroll after 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      canSmoothScrollRef.current = true;
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Scroll to bottom when messages load
  useEffect(() => {
    if (displayMessages.length > 0 && messagesContainerRef.current) {
      if (!canSmoothScrollRef.current) {
        // Instant scroll for first 500ms
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      } else {
        // Smooth scroll after 500ms
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [displayMessages]);

  return (
    <div ref={messagesContainerRef} className="flex-1 space-y-2 overflow-y-auto p-4 scrollbar-hide">
      {displayMessages.map((m) => (
        <Message 
          key={m.id} 
          message={m} 
          currentUserId={currentUserId}
          onRetry={m.isUpload && m.error ? () => onRetryUpload(m.id) : undefined}
          onSetTab={onSetTab}
        />
      ))}
      <div ref={messagesEndRef} />
      {displayMessages.length === 0 && (
        <div className="pt-8 text-center text-xs text-muted-foreground">Say hi 👋</div>
      )}
    </div>
  );
}

export default Messages;
